import { ArrowLeft } from 'lucide-react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { cn } from '@/lib/utils'
import {
  currentPathWithQuery,
  parseBackNav,
  withBackNav,
  type BackNavTarget,
} from '@/lib/backNav'

/**
 * The back control for a detail page reached from more than one list
 * (z8nrz7czdw). Renders nothing at all unless the URL carries a valid `from` —
 * a page opened without one, or with a hostile one, looks exactly as it does
 * today.
 *
 * All parsing and validation lives in `@/lib/backNav`, which is where the tests
 * are: `vitest.config.ts` is `environment: node` / `include: ['src/**\/*.test.ts']`
 * and cannot run a `.tsx`.
 */
export function BackLink({ className }: { className?: string }) {
  const target = useBackNav()
  if (!target) return null

  return (
    <Link
      to={target.to}
      className={cn(
        'inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground',
        className,
      )}
    >
      <ArrowLeft className='h-4 w-4 shrink-0' />
      {target.label}
    </Link>
  )
}

/** The validated back target for the current URL, or null when there is none. */
export function useBackNav(): BackNavTarget | null {
  const [params] = useSearchParams()
  return parseBackNav(params)
}

/**
 * Builds links OUT of the current page that carry it as the origin.
 *
 * The returned `linkTo` passes the current url INCLUDING its own `from`, which
 * is what makes a chain unwind one hop at a time: from the form editor, back
 * reaches the sequence editor still holding the templates list, and back again
 * reaches the list. Without it the second press would strand the user.
 */
export function useBackNavLinkBuilder(label: string): (path: string) => string {
  const location = useLocation()
  const origin = currentPathWithQuery(location)
  return (path: string) => withBackNav(path, origin, label)
}
